import type { ClaimBatchClaimGridRow } from '@cumu/shared';
import {
  billingClaimPrimaryStatusIconClasses,
  claimPrimaryStatusIcon,
  currencyFormat,
  icon,
  numberFormat,
  personName,
  render
} from '@cumu/shared';
import { controller } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import {
  ClientSideGridElement,
  ClientSideGridOptions
} from './client-side-grid-element';
import { dateRange, formatUTCDate, linkRenderer } from './grid-helpers';

@controller('claim-batch-claims')
class ClaimBatchClaimsElement extends ClientSideGridElement<ClaimBatchClaimGridRow> {
  protected getGridOptions(): ClientSideGridOptions<ClaimBatchClaimGridRow> {
    const columnDefs: AgGrid.ColDef<ClaimBatchClaimGridRow>[] = [
      {
        field: 'submitter_claim_control_number',
        headerName: 'Claim',
        cellRenderer: ({
          data,
          node,
          value
        }: AgGrid.ICellRendererParams<ClaimBatchClaimGridRow>) => {
          if (node?.footer) {
            return 'Total';
          }
          return linkRenderer(data?.claim_href, value);
        },
        width: 135,
        minWidth: 135,
        suppressSizeToFit: true
      },
      {
        field: 'person_full_name',
        headerName: 'Person',
        tooltipField: 'person_full_name',
        flex: 1,
        minWidth: 200
      },
      {
        colId: 'primary_status',
        headerName: 'Status',
        valueGetter: ({ data, node }) =>
          node?.footer ? '' : (data?.primary_status_label ?? ''),
        cellRenderer: claimPrimaryStatusRenderer,
        tooltipField: 'primary_status_description',
        width: 190,
        minWidth: 160
      },
      {
        field: 'primary_adjustment_code',
        headerName: 'Adjustment',
        tooltipField: 'primary_adjustment_description',
        width: 140
      },
      {
        field: 'primary_remark_code',
        headerName: 'Remark',
        tooltipField: 'primary_remark_description',
        width: 120
      },
      {
        colId: 'latest_remittance_claim_status',
        headerName: '835 payer status',
        valueGetter: ({ data, node }) => {
          if (node?.footer) {
            return '';
          }
          if (!data?.latest_remittance_claim_status_code) {
            return 'No matching 835';
          }
          return (
            data.latest_remittance_claim_status_description ??
            data.latest_remittance_claim_status_code
          );
        },
        tooltipField: 'latest_remittance_claim_status_description',
        width: 210
      },
      {
        colId: 'latest_claim_acknowledgment_status',
        headerName: '277 acknowledgment',
        valueGetter: ({ data, node }) => {
          if (node?.footer) {
            return '';
          }
          if (!data?.latest_claim_acknowledgment_category_code) {
            return 'No matching 277';
          }
          const category =
            data.latest_claim_acknowledgment_category_description ??
            data.latest_claim_acknowledgment_category_code;
          const status =
            data.latest_claim_acknowledgment_status_description ??
            data.latest_claim_acknowledgment_status_code;
          return status ? `${category}: ${status}` : category;
        },
        tooltipValueGetter: ({ data }) =>
          data?.latest_claim_acknowledgment_effective_date
            ? `Effective ${formatUTCDate(data.latest_claim_acknowledgment_effective_date)}`
            : '',
        width: 240
      },
      {
        field: 'authorization_number',
        headerName: 'Authorization number',
        tooltipField: 'authorization_number',
        width: 190
      },
      {
        field: 'subscriber_id',
        headerName: 'Subscriber ID',
        width: 160
      },
      {
        colId: 'subscriber_name',
        headerName: 'Subscriber',
        valueGetter: ({ data }) =>
          data
            ? personName({
                first_name: data.subscriber_first_name ?? '',
                middle_name: data.subscriber_middle_name ?? '',
                last_name: data.subscriber_last_name,
                suffix: '',
                nickname: ''
              })
            : '',
        flex: 1,
        minWidth: 200
      },
      {
        colId: 'statement_period',
        headerName: 'Statement period',
        valueGetter: ({ data }) =>
          dateRange(data?.statement_period_start, data?.statement_period_end),
        width: 190
      },
      {
        field: 'service_line_count',
        headerName: 'Lines',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatNumber(value),
        aggFunc: 'sum',
        width: 110
      },
      {
        field: 'total_charge_amount',
        headerName: 'Billed',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        aggFunc: 'sum',
        width: 130
      },
      {
        field: 'paid_amount',
        headerName: 'Paid',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        aggFunc: 'sum',
        width: 130
      },
      {
        field: 'adjustment_amount',
        headerName: 'Adjustments',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        aggFunc: 'sum',
        width: 145
      },
      {
        field: 'remittance_issue_count',
        headerName: 'Issues',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatNumber(value),
        aggFunc: 'sum',
        width: 110
      },
      {
        field: 'activity_count',
        headerName: 'Activities',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatNumber(value),
        aggFunc: 'sum',
        width: 125
      }
    ];

    return {
      getRowId: ({ data }) => data.claim_id,
      columnDefs,
      rowClassRules: {
        'color-bg-subtle': ({ node, rowIndex }) =>
          !!node.footer || rowIndex % 2 !== 0,
        'text-bold': ({ node }) => !!node.footer
      },
      grandTotalRow: 'pinnedBottom'
    };
  }
}

function claimPrimaryStatusRenderer({
  data,
  node
}: AgGrid.ICellRendererParams<ClaimBatchClaimGridRow>) {
  if (node?.footer || !data) {
    return '';
  }

  const status = document.createElement('span');
  status.className =
    'd-inline-flex flex-items-center gap-1 width-full min-width-0';
  status.insertAdjacentHTML(
    'beforeend',
    render(
      icon(
        claimPrimaryStatusIcon(data.primary_status),
        `${billingClaimPrimaryStatusIconClasses[data.primary_status]} flex-shrink-0`
      )
    )
  );

  const label = document.createElement('span');
  label.className = 'overflow-ellipses min-width-0';
  label.textContent = data.primary_status_label;
  status.append(label);
  return status;
}

function formatNumber(value: unknown) {
  return numberFormat.format(Number(value ?? 0));
}

function formatCurrency(value: unknown) {
  return currencyFormat.format(Number(value ?? 0));
}

declare global {
  interface Window {
    ClaimBatchClaimsElement: typeof ClaimBatchClaimsElement;
  }
  interface HTMLElementTagNameMap {
    'claim-batch-claims': ClaimBatchClaimsElement;
  }
}
